/**
 * How a pipeline item reads on the board (live brief 4.5): a human title for items that only
 * carry an id, its work state (status.md, "Work states"), and the filter the summary bar and
 * search apply. Pure, so it is tested.
 */
import type { Pipeline, PipelineItem } from '@orgus/protocol';
import type { WorkState } from '@orgus/ui';

/** An id the adapter used as a title: "Submission 1792170681199-15284", or the bare number. */
const ID_TITLE = /^(?:Submission\s+)?#?(\d[\d-]{7,}|[0-9a-f]{12,})$/i;

export interface HumanTitle {
  /** What the card says: the item's own title, or one made from who sent it. */
  title: string;
  /** The raw id when the title was made up, for the detail panel and search. */
  id: string | null;
}

/** "Change from qawl-wt-chat" or "Change 15284" for an item whose title is only an id. */
export function humanTitle(item: Pick<PipelineItem, 'title' | 'by'>): HumanTitle {
  const t = item.title.trim();
  const m = ID_TITLE.exec(t);
  if (!m) return { title: t, id: null };
  const id = m[1]!;
  const short = id.split('-').pop()!.slice(-6);
  return { title: item.by ? `Change from ${item.by}` : `Change ${short}`, id };
}

/** Words that name a checking stage: its active items are verifying, not just running. */
const CHECKING = /verif|test|check|review|lint|build|ci\b/i;

/** The item's work state: queued, running or verifying, landed, failed, or stale (cancelled). */
export function itemWorkState(item: Pick<PipelineItem, 'state' | 'stage'>, p: Pick<Pipeline, 'stages'>): WorkState {
  switch (item.state) {
    case 'queued':
      return 'queued';
    case 'active': {
      const title = p.stages.find((s) => s.id === item.stage)?.title ?? item.stage;
      return CHECKING.test(title) ? 'verifying' : 'running';
    }
    case 'passed':
      return 'landed';
    case 'failed':
      return 'failed';
    default:
      return 'stale';
  }
}

/** The word a state takes on this board: a landing queue's passed items "Landed", cancelled ones "Cancelled". */
export function boardStateLabel(state: WorkState): string | undefined {
  return state === 'stale' ? 'Cancelled' : undefined;
}

export interface BoardFilter {
  state: WorkState | null;
  query: string;
}

/** Whether an item shows under the summary bar's state and the search text (title, sender, id, note). */
export function matchesFilter(item: PipelineItem, p: Pick<Pipeline, 'stages'>, f: BoardFilter, note?: string | null): boolean {
  if (f.state && itemWorkState(item, p) !== f.state) return false;
  const q = f.query.trim().toLowerCase();
  if (!q) return true;
  const h = humanTitle(item);
  return [h.title, item.title, item.by, h.id, note].some((s) => s?.toLowerCase().includes(q));
}

const CONVENTIONAL = /^([a-z]+(?:\([^)]{1,40}\))?!?):\s+(.+)$/i;

/** "fix(chat): keep the draft" to its subject ("Keep the draft") and its type ("fix(chat)"). */
export function commitTitle(title: string): { subject: string; type: string | null } {
  const m = CONVENTIONAL.exec(title.trim());
  if (!m) return { subject: title.trim(), type: null };
  const s = m[2]!.trim();
  return { subject: s.charAt(0).toUpperCase() + s.slice(1), type: m[1]! };
}

export interface QueueGroup<C> {
  state: WorkState;
  /** The group's word: the stage's own name where every item is in one stage ("Verifying"). */
  label: string;
  cards: C[];
  /** The items sit in more than one stage, so each row names its stage. */
  mixedStages: boolean;
}

const GROUP_ORDER: readonly WorkState[] = ['needs-you', 'failed', 'running', 'verifying', 'queued', 'landed', 'stale'];

/**
 * The board's cards as groups by work state, in the order of attention (what failed, what moves,
 * what waits, what landed, what was cancelled). Group labels use the adapter's stage names where
 * they fit, so a landing queue reads Verifying, Queued, Landed.
 */
export function queueGroups<C extends { item: PipelineItem }>(cards: readonly C[], p: Pick<Pipeline, 'stages'>): QueueGroup<C>[] {
  const by = new Map<WorkState, C[]>();
  for (const c of cards) {
    const s = itemWorkState(c.item, p);
    by.set(s, [...(by.get(s) ?? []), c]);
  }
  const title = (id: string) => p.stages.find((x) => x.id === id)?.title ?? id;
  return GROUP_ORDER.filter((s) => by.has(s)).map((state) => {
    const list = by.get(state)!;
    const stages = new Set(list.map((c) => c.item.stage));
    const one = stages.size === 1 ? title([...stages][0]!) : null;
    const label =
      state === 'stale'
        ? 'Cancelled'
        : state === 'failed'
          ? 'Failed'
          : state === 'landed'
            ? (one ?? 'Landed')
            : (state === 'running' || state === 'verifying') && one
              ? one
              : state === 'queued'
                ? 'Queued'
                : state === 'verifying'
                  ? 'Verifying'
                  : 'Running';
    return { state, label, cards: list, mixedStages: stages.size > 1 };
  });
}

export interface PipelineFigures {
  /** Items in flight by work state (finished items excluded). */
  inFlight: Partial<Record<WorkState, number>>;
  inFlightTotal: number;
  /** Finished outcomes, oldest first, for the run strip. */
  outcomes: ('passed' | 'failed' | 'cancelled')[];
  passed: number;
  /** How long each passed item took from submit to land, oldest first. */
  durations: number[];
  /** The median of `durations`, or null with none. */
  medianMs: number | null;
}

/** The numbers above the list: what is in flight, how the last items ended, how long landing takes. */
export function pipelineFigures(p: Pick<Pipeline, 'items' | 'recent' | 'stages'>): PipelineFigures {
  const inFlight: Partial<Record<WorkState, number>> = {};
  let inFlightTotal = 0;
  for (const i of p.items) {
    if (i.state !== 'queued' && i.state !== 'active') continue;
    const s = itemWorkState(i, p);
    inFlight[s] = (inFlight[s] ?? 0) + 1;
    inFlightTotal++;
  }
  const done = p.recent.filter((i): i is PipelineItem & { state: 'passed' | 'failed' | 'cancelled' } => i.state === 'passed' || i.state === 'failed' || i.state === 'cancelled').sort((a, b) => (a.endedAt ?? 0) - (b.endedAt ?? 0));
  const durations = done.filter((i) => i.state === 'passed' && i.startedAt != null && i.endedAt != null && i.endedAt >= i.startedAt).map((i) => i.endedAt! - i.startedAt!);
  const sorted = [...durations].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  const medianMs = sorted.length ? (sorted.length % 2 ? sorted[mid]! : Math.round((sorted[mid - 1]! + sorted[mid]!) / 2)) : null;
  return { inFlight, inFlightTotal, outcomes: done.map((i) => i.state), passed: done.filter((i) => i.state === 'passed').length, durations, medianMs };
}

/** "Verifying" to "Verify", "Deploying" to "Deploy", "Merging" to "Merge": the stage as a noun for "Verify failed". */
export function stageNoun(title: string): string | null {
  const t = title.trim();
  if (!/^[A-Za-z]+$/.test(t)) return null;
  if (!/ing$/i.test(t)) return t;
  let stem = t.slice(0, -3);
  if (stem.length < 2) return null;
  // Running to Run, Shipping to Ship (but Installing keeps its ll, Passing its ss).
  if (/([bdgmnprt])\1$/i.test(stem)) stem = stem.slice(0, -1);
  // Merging to Merge, Staging to Stage, Releasing to Release, Bundling to Bundle.
  else if (/(?:[aeiou][gscvz]|rg|[^aeiou]l)$/i.test(stem) && !/ll$/i.test(stem)) stem += 'e';
  return stem;
}

/** One label per stage for a failure, in rows, pills and banners alike: "Verify failed", or "Failed in waiting for CPU". */
export function failedLabel(stageTitle: string): string {
  const noun = stageNoun(stageTitle);
  return noun ? `${noun.charAt(0).toUpperCase()}${noun.slice(1).toLowerCase()} failed` : `Failed in ${stageTitle.trim().charAt(0).toLowerCase()}${stageTitle.trim().slice(1)}`;
}

/** "conflicts with main in apps/qawl-server/src/routes/approvals.ts" reads "Conflict in approvals.ts": the file, not its path. */
const CONFLICT = /^conflicts? with \S+ in (\S+)$/i;
/** A path's last part: what a short reason names; the whole path stays in the drawer. */
const baseName = (path: string) => path.split(/[\\/]/).pop() || path;
/** "1 spec failed in apps/qawl-server": a count of failures and where, of which a row keeps the count. */
const COUNTED_IN = /^(\d+ \w+ failed) in \S+$/i;

export interface FailureSummary {
  /** The short status a failed row shows: "Checks failed", "Verify failed". */
  label: string;
  /** What failed, for a dim caption cut short: the first failing test ("+2" for more), or the reason. Null when nothing is said. */
  reason: string | null;
}

/** "(checks failed)" at the end of a detail: the phase the protocol adds (toPipeline). */
const PHASE_SUFFIX = /\s*\(([^()]{1,40})\)\s*$/;
/** A sha, then optionally the commit subject, as the first part of a landing's detail. */
const SHA_HEAD = /^[0-9a-f]{7,40}\b/i;
/** "pnpm test:affected failed: a > b; c > d": the command that failed, then its failing tests. */
const FAILED_TESTS = /\bfailed:\s*(.+)$/i;

/**
 * A failed item in a few words (pipelines.md, rows): a short status and the one thing that
 * failed, never the whole failure line. "2d823c4f; pnpm test:affected failed: contracts/a.test.ts
 * > handles every type (checks failed)" reads "Checks failed" and "contracts/a.test.ts > handles
 * every type". The full detail stays in the item's drawer.
 */
export function failureSummary(item: Pick<PipelineItem, 'detail' | 'stage'>, p: Pick<Pipeline, 'stages'>): FailureSummary {
  let text = (item.detail ?? '').trim();
  let phase: string | null = null;
  const m = PHASE_SUFFIX.exec(text);
  if (m) {
    phase = m[1]!.trim();
    text = text.slice(0, m.index).trim();
  }
  // The head (sha and subject) repeats what the row already says.
  const parts = text.split(/;\s+|;$/).map((s) => s.trim()).filter(Boolean);
  if (parts.length > 1 && SHA_HEAD.test(parts[0]!)) parts.shift();
  else if (parts.length === 1 && SHA_HEAD.test(parts[0]!) && parts[0]!.split(/\s+/).length === 1) parts.shift();
  const rest = parts.join('; ');
  const tests = FAILED_TESTS.exec(rest);
  let reason: string | null = rest || null;
  const conflict = CONFLICT.exec(rest);
  if (conflict) reason = `Conflict in ${baseName(conflict[1]!)}`;
  if (tests) {
    const list = tests[1]!.split(/;\s+/).map((s) => s.trim()).filter(Boolean);
    reason = list.length > 1 ? `${list[0]} +${list.length - 1}` : (list[0] ?? null);
  }
  // "2 specs failed in apps/qawl-server" reads "2 specs failed": where is in the drawer.
  const counted = reason ? COUNTED_IN.exec(reason) : null;
  if (counted) reason = counted[1]!;
  const stage = p.stages.find((s) => s.id === item.stage)?.title ?? item.stage;
  const label = phase ? phase.charAt(0).toUpperCase() + phase.slice(1) : failedLabel(stage);
  return { label, reason };
}

/**
 * A finished item's note in a row: its detail without the phase the protocol appends, and for a
 * landing that names its commit ("21e7dd36 Approvals: ...; checked: typecheck, ...") only the
 * commit; what was checked is in the drawer.
 */
export function finishedNote(item: Pick<PipelineItem, 'detail' | 'state'>): string | null {
  const text = (item.detail ?? '').replace(PHASE_SUFFIX, '').trim();
  if (!text) return null;
  const parts = text.split(/;\s+/);
  return item.state === 'passed' && parts.length > 1 && SHA_HEAD.test(parts[0]!) ? parts[0]! : text;
}
